import React, { useState, useEffect } from 'react';
import {
  useParams,
  useLocation,
  Link as RouterLink,
  useNavigate
} from 'react-router-dom';

import {
  Container,
  makeStyles,
  Card,
  Box,
  Button,
  Link,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
  FormControl
} from '@material-ui/core';
import { Download as DownloadIcon } from 'react-feather';

import 'moment/locale/es';
import moment from 'moment';
import { t } from 'src/shared/literals';
import Page from 'src/shared/ui/components/Page';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';
import Form from 'src/shared/ui/components/Form';
import TabPanel from 'src/shared/ui/components/TabPanel';

import CustomerContactDatatable from 'src/customer-contact/ui/components/CustomerContactDatatable';
import CustomerContactForm from 'src/customer-contact/ui/components/CustomerContactForm';

import ContractDatatable from 'src/contract/ui/components/ContractDatatable';
import ContractForm from 'src/contract/ui/components/ContractForm';

import CustomerTermDatatable from 'src/customer-term/ui/components/CustomerTermDatatable';
import CustomerTermForm from 'src/customer-term/ui/components/CustomerTermForm';

import generateCustomerInvoiceList from 'src/customer-invoice/use-cases/generateCustomerInvoiceList';

import CustomerInvoiceDatatable from 'src/customer-invoice/ui/components/CustomerInvoiceDatatable';
import CustomerInvoiceForm from 'src/customer-invoice/ui/components/CustomerInvoiceForm';
import downloadCustomerInvoicePdfById from 'src/customer-invoice/use-cases/downloadCustomerInvoicePdfById';

import validateSchema from 'src/shared/models/SchemaValidator';
import Customer from 'src/customer/models/Customer';

import createCustomer from 'src/customer/use-cases/createCustomer';
import getCustomerById from 'src/customer/use-cases/getCustomerById';
import updateCustomerById from 'src/customer/use-cases/updateCustomerById';

import getCustomerTypeList from 'src/customer-type/use-cases/getCustomerTypeList';

import PaymentDatatable from 'src/payment/ui/components/PaymentDatatable';
import downloadPaymentReceiptPdfById from 'src/payment-receipt/use-cases/downloadPaymentReceiptPdfById';
import downloadCreditNotePdfById from 'src/credit-note/use-cases/downloadCreditNotePdfById';

import uploadFile from 'src/shared/use-cases/uploadFile';
import downloadCustomerDocumentPdfByIndex from 'src/customer/use-cases/downloadCustomerDocumentPdfByIndex';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  },
  formControl: {
    width: '30%',
    margin: theme.spacing(1)
  },
  selectField: {
    paddingBottom: '22px'
  },
  disabledTab: {
    opacity: 0.5
  },
  documentButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  documentButtonRed: {
    background: 'red',
    color: 'white',
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  checkboxFieldDocument: {
    margin: 1,
    paddingTop: '2px'
  },
  documentPending: {
    background: 'red',
    color: 'white'
  },
  documentUploaded: {
    background: 'green',
    color: 'white'
  },
  optionalDocumentUploaded: {
    background: 'orange',
    color: 'white'
  },
  documentTable: {
    textAlign: 'center',
    margin: '5px',
    border: '1px solid'
  },
  documentsDialogContainer: {
    minHeight: '100px'
  },
  dialogListItemSecondary: {
    textAlign: 'center',
    color: 'gray',
    backgroundColor: '#fff',
    padding: '6px 16px',
    marginLeft: '10%',
    width: '80%',
    borderRadius: 5
  },
  dialogListItemPrimary: {
    textAlign: 'center',
    color: '#fff',
    backgroundColor: '#4caf50',
    padding: '6px 16px',
    width: '80%',
    marginLeft: '10%',
    marginTop: '20px',
    borderRadius: 5
  },
  optionalDocumentTable: {
    textAlign: 'center',
    margin: '5px',
    border: '2px solid orange'
  },
  downloadButton: {
    color: 'blue'
  }
}));

const DOCUMENT_TYPES = [];
const OPTIONAL_DOCUMENT_TYPES = [
  'ruc',
  'company_charter',
  'signatories_register',
  'meeting_minute',
  'budget_0',
  'budget_1',
  'budget_2',
  'budget_3'
];

const CustomerFormView = () => {
  const classes = useStyles();

  const navigate = useNavigate();

  const { customerId } = useParams();
  const [companyHeadquarterId, setCompanyHeadquarterId] = useState(
    localStorage.selectedUserHeadquarter
  );
  const location = new URLSearchParams(useLocation().search);

  const [formFields, setFormFields] = useState([]);
  const [customerData, setCustomerData] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  const [filledWizardStepIndex, setFilledWizardStepIndex] = useState(-1);

  const [customerContactId, setCustomerContactId] = useState(0);
  const [contractId, setContractId] = useState(0);
  const [customerTermId, setCustomerTermId] = useState(0);
  const [customerInvoiceId, setCustomerInvoiceId] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);
  const [
    selectedDownloadCreditNoteId,
    setSelectedDownloadCreditNoteId
  ] = useState(null);
  const [
    showSelectDownloadCreditNoteType,
    setShowSelectDownloadCreditNoteType
  ] = useState(false);

  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);

  const [showDocumentUploadDialog, setShowDocumentUploadDialog] = useState(
    false
  );

  const [documentUploadType, setDocumentUploadType] = useState({});

  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [
    showSelectDownloadCustomerInvoiceType,
    setShowSelectDownloadCustomerInvoiceType
  ] = useState(false);
  const [
    selectedDownloadCustomerInvoiceId,
    setSelectedDownloadCustomerInvoiceId
  ] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const getFirstDayOfCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const [invoiceNumberMode, setInvoiceNumberMode] = useState('AUTO');
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState('');
  const [manualDate, setManualDate] = useState(getFirstDayOfCurrentMonth());
  const [generateError, setGenerateError] = useState('');

  const handleUploadDocument = async () => {
    try {
      let file = document.getElementById('file');
      if (!file.files || !file.files[0]) {
        alert('Debe seleccionar un archivo pdf o jpg');
        return;
      }

      file = file.files[0];

      if (
        file.type.indexOf('pdf') === -1 &&
        file.type.indexOf('jpeg') === -1 &&
        file.type.indexOf('jpg') === -1
      ) {
        alert('El formato del archivo debe ser pdf o jpg');
        return;
      }

      if (!Object.keys(documentUploadType).length) {
        alert('Debe seleccionar al menos un tipo de documento');
        return;
      }

      await uploadFile(
        `headquarter-customers/upload/document?headquarterCustomerId=${
          customerData.id
        }&documentType=${Object.keys(documentUploadType).toString()}`,
        file
      );

      let documents = customerData.documents || [];
      const user = JSON.parse(localStorage.user);
      documents.push({
        userFullName: `${user.firstname} ${user.surnames}`,
        timestamp: new Date().getTime(),
        type: Object.keys(documentUploadType)
      });

      let updatedCustomerData = { ...customerData };
      updatedCustomerData.documents = documents;
      alert('Documento subido');
      setCustomerData(updatedCustomerData);

      setShowDocumentUploadDialog(false);
    } catch (e) {
      alert('Ha ocurrido un error subiendo el archivo:' + e);
      setShowDocumentUploadDialog(true);
    }
  };

  const initFormFields = async (fields) => {
    const customerTypeList = await getCustomerTypeList({
      page: null,
      limit: null,
      orderBy: ''
    });

    if (!fields) {
      fields = {
        customerTypeId: customerTypeList[1].id
      };
    }

    const user = JSON.parse(localStorage.user);
    const customer = Customer({
      fields,
      customerTypeList,
      user
    });

    setFormFields([]);
    setFormFields(customer);
  };

  const fetchForm = async () => {
    const response = await getCustomerById(customerId);

    setTabIndex(1);
    setTabIndex(0);

    checkActiveTab();
    initFormFields(response);
    setCustomerData(response);
    setCompanyHeadquarterId(
      localStorage.selectedUserHeadquarter === 'all'
        ? response.companyHeadquarterId
        : localStorage.selectedUserHeadquarter
    );
  };

  const checkActiveTab = () => {
    if (location && location.get('activeTabIndex') === '1') {
      setFilledWizardStepIndex(0);

      setTimeout(() => {
        changeTabIndex(parseInt(location.get('activeTabIndex')));
      }, 0);
    }
  };

  useEffect(() => {
    if (customerId === 'new') {
      initFormFields();
    } else {
      fetchForm();
    }
  }, []);

  const changeTabIndex = async (index) => {
    if (customerId === 'new' && filledWizardStepIndex < index - 1) {
      return;
    }

    if (index === 1) {
      // show contacts datatable
    }

    setTabIndex(index);
  };

  const handleTabChange = (event, index) => {
    changeTabIndex(index);
  };

  const a11yProps = (index) => {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`
    };
  };

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );

    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues, target) => {
    if (target.name === 'customerTypeId') {
      let fields = {};
      formFieldsValues.forEach((x) => {
        fields[x.name] = x.value;
      });
      fields[target.name] = target.value;

      initFormFields(fields);

      return;
    } else if (target.name === 'status') {
      let fields = {};
      formFieldsValues.forEach((x) => {
        const item = {
          [x.name]: x.value
        };
        fields = {
          ...fields,
          ...item
        };
      });

      initFormFields(fields);

      return;
    }

    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    let validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      console.log(validatedForm.errors);
      alert('Rellene todos los campos correctamente.');
      return;
    }

    validatedForm.fields.companyHeadquarterId = companyHeadquarterId;

    setIsRequesting(true);
    if (customerId === 'new') {
      const result = await createCustomer(validatedForm.fields);
      setIsRequesting(false);

      if (result && result.error) {
        alert(t(result.error));
        return;
      } else if (!result.id) {
        alert('Se ha producido un error, vuelva a intentarlo');
        return;
      }

      navigate(`/app/customers`, {
        replace: true
      });

      setTimeout(() => {
        const redirectLink = ['ACTIVATE'].includes(
          validatedForm.fields.status?.toUpperCase()
        )
          ? `/app/customers/${result.id}?activeTabIndex=1`
          : '/app/customers';
        navigate(redirectLink, {
          replace: true
        });
      }, 0);

      return;
    }

    const result = await updateCustomerById(validatedForm.fields, customerId);
    setIsRequesting(false);

    if (!result.id) {
      alert('Se ha producido un error, vuelva a intentarlo');
      return;
    }

    const redirectLink = ['ACTIVATE'].includes(
      validatedForm.fields.status?.toUpperCase()
    )
      ? `/app/customers/${result.id}?activeTabIndex=1`
      : '/app/customers';

    navigate(`/app/customers`, {
      replace: true
    });

    setTimeout(() => {
      navigate(redirectLink, {
        replace: true
      });
    }, 0);
  };

  const handleEditCustomerContact = (id) => {
    setCustomerContactId(id);
  };

  const handleAddCustomerContact = () => {
    setCustomerContactId('new');
  };

  const handleCustomerContactFormCancel = () => {
    setCustomerContactId(0);
  };

  const handleCustomerContactFormSuccess = () => {
    setCustomerContactId(0);
  };

  const getCustomerDisplayName = () => {
    if (!Array.isArray(formFields) || !formFields.length) {
      return '';
    }

    const getVal = (fieldName) =>
      formFields.find((f) => f.name === fieldName)?.value || '';

    const businessName = getVal('businessName');
    const firstname = getVal('firstname');
    const surnames = getVal('surnames');
    const fullName = `${firstname} ${surnames}`.trim();

    return businessName || fullName || getVal('document') || '';
  };

  const handleEditContract = (id) => {
    setContractId(id);
  };

  const handleAddContract = () => {
    setContractId('new');
  };

  const handleContractFormCancel = () => {
    setContractId(0);
  };

  const handleContractFormSuccess = () => {
    setContractId(0);
  };

  const handleEditCustomerTerm = (id) => {
    setCustomerTermId(id);
  };

  const handleEditCustomerInvoice = (id) => {
    setCustomerInvoiceId(id);
  };

  const handleCustomerInvoiceFormCancel = () => {
    setCustomerInvoiceId(0);
  };

  const handleCustomerInvoiceFormSuccess = () => {
    setCustomerInvoiceId(0);
  };

  const handleCustomerTermFormCancel = () => {
    setCustomerTermId(0);
  };

  const handleCustomerTermFormSuccess = () => {
    setCustomerTermId(0);
  };

  const handleDownloadCustomerInvoice = async (id) => {
    setSelectedDownloadCustomerInvoiceId(id);
    setShowSelectDownloadCustomerInvoiceType(true);
  };

  const handleDialogSelectDownloadCustomerInvoiceType = (type) => {
    setShowSelectDownloadCustomerInvoiceType(false);
    downloadCustomerInvoicePdfById(selectedDownloadCustomerInvoiceId, type);
  };

  const handleDialogCloseDownloadCustomerInvoice = async (id) => {
    setShowSelectDownloadCustomerInvoiceType(false);
  };

  const handleGenerateCustomerInvoiceList = async () => {
    setInvoiceNumberMode('AUTO');
    setManualInvoiceNumber('');
    setManualDate(getFirstDayOfCurrentMonth());
    setGenerateError('');
    setShowGenerateDialog(true);
  };

  const handleGenerateDialogConfirm = async () => {
    if (isRequesting) return;
    setIsRequesting(true);

    try {
      if (invoiceNumberMode === 'MANUAL') {
        if (!manualInvoiceNumber || manualInvoiceNumber.length > 10) {
          setGenerateError('El número debe tener entre 1 y 10 dígitos');
          return;
        }
      }

      setShowGenerateDialog(false);

      const result = await generateCustomerInvoiceList({
        companyHeadquarterId,
        customerId,
        manualInvoiceNumber:
          invoiceNumberMode === 'MANUAL' ? manualInvoiceNumber : undefined,
        manualDate: invoiceNumberMode === 'MANUAL' ? manualDate : undefined
      });
      if (result) {
        if (result.error) {
          alert(t(result.error));
        } else if (result.length) {
          alert(`Se crearon ${result.length} facturas satisfactoriamente`);

          setTabIndex(3);
          setTabIndex(4);
        } else {
          alert('No se encontraron cuotas pendientes en el período actual');
        }
      } else {
        alert('Ha ocurrido un problema creando las facturas.');
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleGenerateDialogCancel = () => {
    setShowGenerateDialog(false);
  };

  const handleAddPayment = (invoiceId) => {
    //setInvoiceId(invoiceId);
    //setShowAddPaymentDialog(true);
  };

  const handleDialogCloseAddPayment = () => {
    //setShowAddPaymentDialog(false);
  };

  const handleDownloadPayment = (id, row) => {
    switch (row.PaymentType.code) {
      case 'CREDIT_NOTE':
        setSelectedDownloadCreditNoteId(id);
        setShowSelectDownloadCreditNoteType(true);
        break;
      default:
        setShowSelectDownloadCreditNoteType(false);
        downloadPaymentReceiptPdfById(id);
    }
  };

  const handleDownloadCreditNoteByType = (type) => {
    downloadCreditNotePdfById(selectedDownloadCreditNoteId, type);
    setShowSelectDownloadCreditNoteType(false);
  };

  const handleDialogCloseDownloadCreditNote = async (id) => {
    setShowSelectDownloadCreditNoteType(false);
  };

  const handleCloseDocumentsDialog = () => {
    setShowDocumentsDialog(false);
  };

  const handleShowUploadDocument = () => {
    setDocumentUploadType({});
    setShowDocumentUploadDialog(true);
  };

  const handleCloseUploadDocumentDialog = () => {
    setShowDocumentsDialog(false);
    setShowDocumentUploadDialog(false);
    setDocumentUploadType({});
  };

  const hasCustomerDocumentType = (documentType) => {
    return (
      customerData &&
      customerData.documents &&
      customerData.documents.some((document) =>
        document.type.includes(documentType)
      )
    );
  };

  const hasCustomerSomePendingDocument = () => {
    if (!customerData) {
      return true;
    }

    const { documents } = customerData;

    if (!documents || !documents.length) {
      return true;
    }

    let documentsCount = [];
    for (const document of documents) {
      const documentType =
        typeof document.type === 'object' ? document.type : [document.type];
      const mandatoryDocumentType = documentType
        .filter((x) => (DOCUMENT_TYPES.includes(x) ? x : null))
        .filter((x) => x);

      documentsCount = documentsCount.concat(mandatoryDocumentType);
    }

    const uniqueDocumentsCount = Array.from(new Set(documentsCount));

    return uniqueDocumentsCount.length < DOCUMENT_TYPES.length;
  };

  const handleClickDownloadDocumentByIndex = (documentIndex) => {
    downloadCustomerDocumentPdfByIndex({
      headquarterCustomerId: customerData.id,
      documentIndex
    });
  };

  const renderDocuments = () => {
    return (
      <table className={classes.documentTable}>
        <thead>
          <th className={classes.documentTable}>ESTADO</th>
          <th></th>

          {DOCUMENT_TYPES.map((documentType) => (
            <th className={classes.documentTable}>
              {t(documentType).toUpperCase()}
            </th>
          ))}
          {OPTIONAL_DOCUMENT_TYPES.map((documentType) => (
            <th className={classes.optionalDocumentTable}>
              {t(documentType).toUpperCase()}
            </th>
          ))}
        </thead>
        <tbody>
          <tr className={classes.documentTable}>
            {hasCustomerSomePendingDocument() ? (
              <td className={classes.documentPending}>PENDIENTE</td>
            ) : (
              <td className={classes.documentUploaded}>OK</td>
            )}

            <td></td>
            {DOCUMENT_TYPES.map((documentType) =>
              hasCustomerDocumentType(documentType) ? (
                <td className={classes.documentUploaded}>OK</td>
              ) : (
                <td className={classes.documentPending}>X</td>
              )
            )}

            {OPTIONAL_DOCUMENT_TYPES.map((documentType) =>
              hasCustomerDocumentType(documentType) ? (
                <td className={classes.optionalDocumentUploaded}>OK</td>
              ) : (
                <td></td>
              )
            )}
          </tr>

          <tr>
            <td colSpan="9">
              <hr></hr>
            </td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>

          {customerData?.documents ? (
            customerData.documents.map((customerDocumentType, index) => (
              <tr>
                <td className={classes.documentTable}>
                  {moment(customerDocumentType.timestamp)
                    .locale('es')
                    .format('DD/MM/YYYY hh:mm:ss')}
                  <br></br>
                  {customerDocumentType.userFullName || ''}
                </td>
                <td>
                  <SvgIcon
                    className={classes.downloadButton}
                    onClick={(event) =>
                      handleClickDownloadDocumentByIndex(index)
                    }
                    fontSize="small"
                    color="action"
                  >
                    <DownloadIcon />
                  </SvgIcon>
                </td>

                {DOCUMENT_TYPES.map((documentType) =>
                  customerDocumentType.type.includes(documentType) ? (
                    <td className={classes.documentUploaded}>OK</td>
                  ) : (
                    <td className={classes.documentTable}> </td>
                  )
                )}

                {OPTIONAL_DOCUMENT_TYPES.map((documentType) =>
                  customerDocumentType.type.includes(documentType) ? (
                    <td className={classes.documentUploaded}>OK</td>
                  ) : (
                    <td className={classes.documentTable}> </td>
                  )
                )}
              </tr>
            ))
          ) : (
            <></>
          )}
        </tbody>
      </table>
    );
  };

  const handleSelectCustomerDocumentType = (event) => {
    const { name } = event.target;

    let values = documentUploadType;

    if (values[name]) {
      delete values[name];
    } else {
      values[name] = name;
    }

    console.log(values);
    setDocumentUploadType(values);
  };

  const isDocumentTypeChecked = (type) => documentUploadType[type];

  const renderSelectCustomerDocumentType = () => {
    const name = 'document-type';

    return (
      <FormControl className={classes.formControl} key={name}>
        {documentUploadType &&
          DOCUMENT_TYPES.concat(OPTIONAL_DOCUMENT_TYPES).map((option) => (
            <FormControlLabel
              className={classes.checkboxFieldDocument}
              value={t(option)}
              control={
                <Checkbox
                  name={option}
                  color="primary"
                  checked={isDocumentTypeChecked(option)}
                  onChange={handleSelectCustomerDocumentType}
                />
              }
              label={t(option).toUpperCase()}
              labelPlacement="end"
            />
          ))}
      </FormControl>
    );
  };

  const renderDocumentsDialog = () => {
    return (
      <Dialog
        className={classes.documentsDialog}
        onClose={handleCloseDocumentsDialog}
        aria-labelledby="documents-dialog"
        open={showDocumentsDialog}
        maxWidth="false"
      >
        <div className={classes.documentsDialogContainer}>
          <DialogTitle id="documents-dialog">Documentos</DialogTitle>

          {showDocumentUploadDialog ? (
            <List>
              <input type="file" id="file" name="file" />
              <br />
              {renderSelectCustomerDocumentType()}
              <ListItem
                className={classes.dialogListItemPrimary}
                button
                onClick={handleUploadDocument}
                key={'UPLOAD_DOCUMENT'}
              >
                <ListItemText primary={'Subir'} />
              </ListItem>
              <ListItem
                className={classes.dialogListItemSecondary}
                button
                onClick={handleCloseUploadDocumentDialog}
                key={'CANCEL_UPLOAD_DOCUMENT'}
              >
                <ListItemText primary={'Cancelar'} />
              </ListItem>
            </List>
          ) : (
            <>
              {renderDocuments()}

              <List>
                <ListItem
                  className={classes.dialogListItemPrimary}
                  button
                  onClick={handleShowUploadDocument}
                  key={'ADD_DOCUMENT'}
                >
                  <ListItemText primary={'Añadir documento'} />
                </ListItem>
                <ListItem
                  className={classes.dialogListItemSecondary}
                  button
                  onClick={handleCloseDocumentsDialog}
                  key={'CANCEL_ADD_DOCUMENT'}
                >
                  <ListItemText primary={'Cancelar'} />
                </ListItem>
              </List>
            </>
          )}
        </div>
      </Dialog>
    );
  };

  const handleShowDocuments = () => {
    setShowDocumentsDialog(true);
  };

  const renderPaymentDialog = () => {
    return (
      <Dialog
        onClose={handleDialogCloseDownloadCreditNote}
        aria-labelledby="simple-dialog-title"
        open={showSelectDownloadCreditNoteType}
      >
        <DialogTitle id="simple-dialog-title">
          Seleccione el tipo de nota de crédito
        </DialogTitle>
        <List>
          <ListItem
            button
            onClick={() =>
              handleDownloadCreditNoteByType('ORIGINAL_AND_DUPLICATE')
            }
            key={'ORIGINAL_AND_DUPLICATE'}
          >
            <ListItemText primary={'Original'} />
          </ListItem>
          <ListItem
            button
            onClick={() => handleDownloadCreditNoteByType('CUSTOMER')}
            key={'CUSTOMER'}
          >
            <ListItemText primary={'Digital'} />
          </ListItem>
        </List>
      </Dialog>
    );
  };

  return (
    <Page className={classes.root} title="Cliente">
      {renderDocumentsDialog()}
      {renderPaymentDialog()}

      <Dialog
        onClose={handleDialogCloseDownloadCustomerInvoice}
        aria-labelledby="simple-dialog-title"
        open={showSelectDownloadCustomerInvoiceType}
      >
        <DialogTitle id="simple-dialog-title">
          Seleccione el tipo de factura
        </DialogTitle>
        <List>
          <ListItem
            button
            onClick={() =>
              handleDialogSelectDownloadCustomerInvoiceType(
                'ORIGINAL_AND_DUPLICATE'
              )
            }
            key={'ORIGINAL_AND_DUPLICATE'}
          >
            <ListItemText primary={'Original'} />
          </ListItem>
          <ListItem
            button
            onClick={() =>
              handleDialogSelectDownloadCustomerInvoiceType('CUSTOMER')
            }
            key={'CUSTOMER'}
          >
            <ListItemText primary={'Digital'} />
          </ListItem>
        </List>
      </Dialog>

      <Dialog
        onClose={handleDialogCloseAddPayment}
        aria-labelledby="simple-dialog-title"
        open={showAddPaymentDialog}
      >
        <DialogTitle id="simple-dialog-title">Registrar recibo</DialogTitle>
      </Dialog>

      <Dialog
        onClose={handleGenerateDialogCancel}
        aria-labelledby="generate-dialog-title"
        open={showGenerateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="generate-dialog-title">
          Generar facturación
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ¿Está seguro? Asegúrese de que todos los contratos hayan sido
            aprobados y dados de alta antes de proceder.
          </Typography>

          <Box mt={2}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                Generar nº de factura:
              </Typography>
              <RadioGroup
                value={invoiceNumberMode}
                onChange={(e) => setInvoiceNumberMode(e.target.value)}
              >
                <FormControlLabel
                  value="AUTO"
                  control={<Radio />}
                  label="Automáticamente"
                />
                <FormControlLabel
                  value="MANUAL"
                  control={<Radio />}
                  label="Manualmente"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {invoiceNumberMode === 'MANUAL' && (
            <>
              <Box mt={1}>
                <TextField
                  autoFocus
                  label="Nº de factura"
                  type="text"
                  value={manualInvoiceNumber}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Only allow positive integers, max 10 digits
                    if (val === '' || /^\d{1,10}$/.test(val)) {
                      setManualInvoiceNumber(val);
                      setGenerateError('');
                    }
                  }}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  error={!!generateError}
                  helperText={generateError}
                  fullWidth
                />
              </Box>

              <Box mt={2}>
                <TextField
                  label="Fecha de factura"
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGenerateDialogCancel} color="default">
            Cancelar
          </Button>
          <Button
            onClick={handleGenerateDialogConfirm}
            color="primary"
            variant="contained"
          >
            Generar
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="/app/customers">
              Clientes
            </Link>
            {getCustomerDisplayName() ? ` > ${getCustomerDisplayName()}` : ''}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Link component={RouterLink} to="/app/customers">
            <Button className={classes.backButton}>
              {customerId === 'new' ? 'Volver' : 'Volver a listado'}
            </Button>
          </Link>

          {customerData?.id && (
            <Button
              color={'primary'}
              className={
                hasCustomerSomePendingDocument()
                  ? classes.documentButtonRed
                  : classes.documentButton
              }
              onClick={handleShowDocuments}
            >
              {'Documentos'}
            </Button>
          )}

          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            {customerId === 'new' ? 'Siguiente' : 'Guardar cliente'}
          </Button>
        </Box>
        <Card>
          <form>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              aria-label="full width tabs example"
            >
              <Tab label={`Datos`} {...a11yProps(0)} />

              {customerData?.status?.toUpperCase().indexOf('ACTIVE') !== -1 && (
                <Tab label="Contactos" {...a11yProps(1)} />
              )}

              {customerData?.status?.toUpperCase().indexOf('ACTIVE') !== -1 && (
                <Tab label="Contratos" {...a11yProps(2)} />
              )}

              {customerData?.status?.toUpperCase().indexOf('ACTIVE') !== -1 && (
                <Tab label="Cuotas" {...a11yProps(3)} />
              )}

              {customerData?.status?.toUpperCase().indexOf('ACTIVE') !== -1 && (
                <Tab label="Facturación" {...a11yProps(4)} />
              )}

              {customerData?.status?.toUpperCase().indexOf('ACTIVE') !== -1 && (
                <Tab
                  className={customerId === 'new' ? classes.disabledTab : ''}
                  label="Cobranzas"
                  {...a11yProps(5)}
                />
              )}
            </Tabs>

            <TabPanel value={tabIndex} index={0}>
              <Box className={classes.tabPanelData}>
                <Form
                  fields={formFields}
                  onChangeFields={handleChangeFormFieldsValues}
                />
              </Box>
            </TabPanel>
            <TabPanel value={tabIndex} index={1}>
              {!customerContactId ? (
                <CustomerContactDatatable
                  customerId={customerId}
                  handleEdit={handleEditCustomerContact}
                  handleAdd={handleAddCustomerContact}
                />
              ) : (
                <CustomerContactForm
                  customerId={customerId}
                  customerContactId={customerContactId}
                  cancel={handleCustomerContactFormCancel}
                  success={handleCustomerContactFormSuccess}
                />
              )}
            </TabPanel>
            <TabPanel value={tabIndex} index={2}>
              {!contractId ? (
                <ContractDatatable
                  customerId={customerId}
                  handleEdit={handleEditContract}
                  handleAdd={handleAddContract}
                />
              ) : (
                <ContractForm
                  customerId={customerId}
                  contractId={contractId}
                  cancel={handleContractFormCancel}
                  success={handleContractFormSuccess}
                />
              )}
            </TabPanel>
            <TabPanel value={tabIndex} index={3}>
              {!customerTermId ? (
                <CustomerTermDatatable
                  customerId={customerId}
                  handleEdit={handleEditCustomerTerm}
                />
              ) : (
                <CustomerTermForm
                  customerId={customerId}
                  customerTermId={customerTermId}
                  cancel={handleCustomerTermFormCancel}
                  success={handleCustomerTermFormSuccess}
                />
              )}
            </TabPanel>
            <TabPanel value={tabIndex} index={4}>
              <div>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleGenerateCustomerInvoiceList}
                  >
                    Generar facturación
                  </Button>
                </Box>
              </div>
              {!customerInvoiceId ? (
                <CustomerInvoiceDatatable
                  customerId={customerId}
                  handleDownload={handleDownloadCustomerInvoice}
                  handleAddPayment={handleAddPayment}
                  handleEdit={handleEditCustomerInvoice}
                />
              ) : (
                <CustomerInvoiceForm
                  customerId={customerId}
                  customerInvoiceId={customerInvoiceId}
                  cancel={handleCustomerInvoiceFormCancel}
                  success={handleCustomerInvoiceFormSuccess}
                />
              )}
            </TabPanel>
            <TabPanel value={tabIndex} index={5}>
              <PaymentDatatable
                customerId={customerId}
                handleDownload={handleDownloadPayment}
                showAddButton={false}
              />
            </TabPanel>
          </form>
        </Card>
      </Container>
    </Page>
  );
};

export default CustomerFormView;
